import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import '../journal.css'
import { LogoMark, Wordmark, PhotoPrint, Mono, Rule } from '../components/ui'
import { useSession } from '@/client/lib/auth'

// Apply cream background to body on mount, restore on unmount
function useCreambody() {
  useEffect(() => {
    document.body.classList.add('db-body-bg')
    return () => document.body.classList.remove('db-body-bg')
  }, [])
}

export function JournalLandingPage() {
  useCreambody()
  const { data: session } = useSession()
  const isAuthed = !!session?.user

  return (
    <div className="db-page">
      <StickyNav isAuthed={isAuthed} />

      <main id="top">
        <HeroSection isAuthed={isAuthed} />
        <GallerySection />
        <PromisesSection />
        <ShapeSection />
        <CtaSection isAuthed={isAuthed} />
      </main>

      <SiteFooter />
    </div>
  )
}

/* ── Sticky navigation ─────────────────────────────────────────────────── */
function StickyNav({ isAuthed }: { isAuthed: boolean }) {
  const navigate = useNavigate()
  return (
    <nav className="db-nav" role="navigation">
      <div className="db-wrap db-nav-row">
        <a
          className="db-nav-brand"
          href="/#top"
          onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
        >
          <LogoMark size={30} />
          <span style={{
            fontFamily: 'var(--db-serif-display)',
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: '-0.005em',
            color: 'var(--db-ink)',
          }}>
            Daybook
          </span>
        </a>
        <div className="db-nav-links">
          <a href="#example">An example</a>
          <a href="#shape">What it is</a>
          <a href="#principles">Principles</a>
          {isAuthed ? (
            <button
              onClick={() => navigate('/journal/dashboard')}
              className="db-nav-pill"
              style={{ background: 'none', cursor: 'pointer' }}
            >
              Open journal
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate('/sign-in')}
                style={{ background: 'none', border: 'none', fontFamily: 'var(--db-mono)', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--db-ink-2)', cursor: 'pointer', padding: '0 4px' }}
              >
                Sign in
              </button>
              <button
                onClick={() => navigate('/sign-up')}
                className="db-nav-pill"
                style={{ background: 'none', cursor: 'pointer' }}
              >
                Begin a journal
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}

/* ── Hero ──────────────────────────────────────────────────────────────── */
function HeroSection({ isAuthed }: { isAuthed: boolean }) {
  const navigate = useNavigate()
  return (
    <section className="db-hero" id="begin">
      <div className="db-wrap">
        <div style={{ marginBottom: 26 }}>
          <Mono style={{ fontSize: 12, letterSpacing: '0.14em' }}>
            A private daybook · since 2025
          </Mono>
        </div>
        <h1>
          Every little moment,<br />
          <em>remembered.</em>
        </h1>
        <p style={{
          fontFamily: 'var(--db-serif-body)',
          fontSize: 'clamp(17px, 2.5vw, 23px)',
          lineHeight: 1.5,
          color: 'var(--db-ink-2)',
          maxWidth: 600,
          margin: '26px auto 0',
        }}>
          Photographs, short videos, and a few sentences at the end of the day —
          gathered as one record, on one page, that you own forever.
          No feed. No audience. No performance.
        </p>

        {isAuthed ? (
          <div style={{ marginTop: 48, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="db-btn-solid" onClick={() => navigate('/journal/dashboard')}>
              Open your journal
            </button>
          </div>
        ) : (
          <>
            <HandleClaim />
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <Mono style={{ fontSize: 10, color: 'var(--db-ink-3)' }}>
                Already keeping a journal?{' '}
                <a
                  href="/sign-in"
                  style={{ color: 'var(--db-accent-ink)', textDecoration: 'none', borderBottom: '1px solid color-mix(in srgb, var(--db-accent) 40%, transparent)' }}
                >
                  Sign in
                </a>
              </Mono>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

function HandleClaim() {
  const [name, setName] = useState('')
  const navigate = useNavigate()
  const slug = (name.trim().toLowerCase().split(/\s+/)[0] ?? '').replace(/[^a-z0-9]/g, '') || 'name'

  const begin = () => {
    if (!name.trim()) return
    navigate('/sign-up', { state: { journalName: name.trim(), handle: slug } })
  }

  return (
    <div className="db-claim">
      <div className="db-claim-field">
        <span className="db-claim-prefix">daybooklog.com /</span>
        <input
          className="db-claim-input"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') begin() }}
          placeholder="your child's name"
          autoComplete="off"
        />
        <button className="db-claim-btn" onClick={begin}>
          Begin the path
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <Mono style={{ fontSize: 10, letterSpacing: '0.12em' }}>
          private from the first day · share the link only with who you choose
        </Mono>
      </div>
    </div>
  )
}

/* ── Gallery — peek at a real journal ─────────────────────────────────── */
function GallerySection() {
  const navigate = useNavigate()
  return (
    <section id="example">
      <div className="db-wrap">
        <div className="db-gallery">
          <figure>
            <PhotoPrint photo={{ label: 'The first hand.', ratio: '3/2', tone: 'warm', url: '/cairn/cairn-01-born.jpg' }} />
            <figcaption>She held on from the first second. We weren't expecting that.</figcaption>
            <div className="db-gallery-date">14 Aug 2025</div>
          </figure>
          <figure className="db-gallery-lift">
            <PhotoPrint photo={{ label: 'First week home.', ratio: '4/5', tone: 'warm', url: '/cairn/cairn-02-home.jpg' }} />
            <figcaption>First week home. The weight of her felt exactly right.</figcaption>
            <div className="db-gallery-date">21 Aug 2025</div>
          </figure>
          <figure>
            <PhotoPrint photo={{ label: 'First crawl.', ratio: '1/1', tone: 'greige', url: '/cairn/cairn-05-crawl.jpg' }} />
            <figcaption>The day she figured out she could go somewhere. The room changed after that.</figcaption>
            <div className="db-gallery-date">8 Dec 2025</div>
          </figure>
        </div>

        <div style={{ textAlign: 'center', paddingBottom: 64 }}>
          <button
            onClick={() => navigate('/cairn')}
            style={{
              background: 'none',
              border: '1px solid var(--db-line)',
              borderRadius: 999,
              padding: '13px 28px',
              fontFamily: 'var(--db-mono)',
              fontSize: 11,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--db-accent-ink)',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--db-paper-lift)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
          >
            See an example journal →
          </button>
        </div>
      </div>
    </section>
  )
}

/* ── Promises — what it is / isn't ────────────────────────────────────── */
function PromisesSection() {
  const promises = [
    ['No feed, no algorithm', 'Reverse-chronological. The newest page first, the rest where you left them.'],
    ['No accounts to read', 'Family open a private link and leave a named note. No sign-up, ever.'],
    ['No metrics, no ads', 'Nothing to perform for. No likes, no counts, nothing to sell.'],
    ['Yours to keep', 'Export every photo and word as a zip. Bind it into a book one day.'],
  ] as [string, string][]

  return (
    <section className="db-promises" id="principles">
      <div className="db-wrap">
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Mono style={{ fontSize: 11, letterSpacing: '0.18em' }}>Four commitments</Mono>
        </div>
        <div className="db-promises-grid">
          {promises.map(([h, b]) => (
            <div key={h} className="db-promise-cell">
              <h3>{h}</h3>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Shape — how a life gets kept ─────────────────────────────────────── */
function ShapeSection() {
  const defs = [
    ['01', 'The Journal', 'One child, one page. A private place owned entirely by the parents.'],
    ['02', 'The Entry', 'A photograph or video with a few sentences — or just sentences. Dated to when the moment happened, not when you posted.'],
    ['03', 'The Set', 'A named chapter that groups entries together. A trip, a milestone, a season — optional, never required.'],
    ['04', 'The Share Link', 'A revocable token for family. Click the link, see the journal — no account, no sign-up. Roll it if it ever leaks.'],
  ] as [string, string, string][]

  return (
    <section className="db-shape" id="shape">
      <div className="db-wrap">
        <Mono style={{ fontSize: 11, letterSpacing: '0.18em' }}>How a life gets kept</Mono>
        <h2 style={{ marginTop: 18 }}>Small, intentional, yours.</h2>
        <p style={{
          fontFamily: 'var(--db-serif-body)',
          fontSize: 20,
          color: 'var(--db-ink-2)',
          maxWidth: 560,
          margin: '0 auto',
          lineHeight: 1.5,
        }}>
          Four ideas that make a journal. Nothing more, nothing less.
        </p>

        <div className="db-defs">
          {defs.map(([num, title, body]) => (
            <div key={num}>
              <div className="db-def-num">{num}</div>
              <div className="db-def-title">{title}</div>
              <p className="db-def-body">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── CTA ───────────────────────────────────────────────────────────────── */
function CtaSection({ isAuthed }: { isAuthed: boolean }) {
  const navigate = useNavigate()
  return (
    <section className="db-cta">
      <div className="db-wrap">
        <LogoMark size={38} />
        <h2>{isAuthed ? 'Back to your journal.' : "Begin your child's journal."}</h2>
        <p>
          The first entry is the hardest. After that, it's just the end of the day,
          a photograph, and a few sentences.
        </p>
        <div className="db-cta-btns">
          {isAuthed ? (
            <button className="db-btn-solid" onClick={() => navigate('/journal/dashboard')}>
              Open journal
            </button>
          ) : (
            <>
              <button className="db-btn-solid" onClick={() => navigate('/sign-up')}>
                Begin a journal
              </button>
              <button className="db-btn-ghost" onClick={() => navigate('/sign-in')}>
                Sign in
              </button>
            </>
          )}
        </div>
        {!isAuthed && (
          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => navigate('/cairn')}
              style={{ background: 'none', border: 'none', fontFamily: 'var(--db-serif-body)', fontStyle: 'italic', fontSize: 15, color: 'var(--db-ink-3)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              See an example journal
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

/* ── Site footer ───────────────────────────────────────────────────────── */
function SiteFooter() {
  return (
    <footer className="db-foot">
      <div className="db-wrap db-foot-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={22} />
          <Wordmark size={18} sub={false} />
        </div>
        <div className="db-foot-links">
          <a href="/cairn">Example journal</a>
          <a href="/sign-in">Sign in</a>
          <a href="/sign-up">Begin a journal</a>
        </div>
      </div>
      <div className="db-wrap" style={{ marginTop: 32 }}>
        <Rule />
        <p style={{
          fontFamily: 'var(--db-serif-body)',
          fontStyle: 'italic',
          fontSize: 15,
          color: 'var(--db-ink-3)',
          textAlign: 'center',
          marginTop: 22,
        }}>
          Made with love. Begun the eighteenth of November, 2025.
        </p>
      </div>
    </footer>
  )
}
